if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Sync thumbnails when the main gallery is scrolled/swiped on mobile
        if (this.elements.viewer.slider) {
          let scrollTimeout;
          this.elements.viewer.slider.addEventListener('scroll', () => {
            if (this.mql.matches) return;
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              const slider = this.elements.viewer.slider;
              const items = this.getMediaItems();
              if (!items.length) return;
              const sliderLeft = slider.scrollLeft;
              let closest = items[0];
              let closestDist = Infinity;
              for (const item of items) {
                const dist = Math.abs(item.offsetLeft - sliderLeft);
                if (dist < closestDist) {
                  closestDist = dist;
                  closest = item;
                }
              }
              const mediaId = closest.dataset.mediaId;
              if (!mediaId) return;
              const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (thumbnail) this.setActiveThumbnail(thumbnail);
            }, 50);
          });
        }

        this.setupGalleryNav();
      }

      setupGalleryNav() {
        this.navPrevButton = this.elements.viewer.querySelector('button[name="previous"]');
        this.navNextButton = this.elements.viewer.querySelector('button[name="next"]');
        if (!this.navPrevButton || !this.navNextButton) return;

        // Detach SliderComponent's scroll-based button control — on desktop the
        // gallery is stacked so scroll visibility checks wrongly disable both buttons.
        this.elements.viewer.prevButton = null;
        this.elements.viewer.nextButton = null;

        // Intercept arrow clicks in capture phase to prevent SliderComponent's
        // scroll-based handler from firing. On desktop the gallery is stacked
        // (all items at offsetLeft 0) so scroll-based navigation breaks — we
        // use class-based (is-active) navigation instead.
        this.elements.viewer.addEventListener('click', (e) => {
          const btn = e.target.closest('button[name="previous"], button[name="next"]');
          if (!btn || !this.mql.matches) return;

          e.stopPropagation();
          e.preventDefault();

          const direction = btn.name === 'next' ? 1 : -1;
          this.navigateMedia(direction);
        }, true);

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            setTimeout(() => this.updateGalleryNavButtons(), 50);
          });
        });

        if (this.mql.matches) this.updateGalleryNavButtons();
        this.mql.addEventListener('change', () => {
          if (this.mql.matches) this.updateGalleryNavButtons();
        });

        this.setupKeyboardNav();
      }

      setupKeyboardNav() {
        this.isInViewport = false;
        const io = new IntersectionObserver((entries) => {
          this.isInViewport = entries[0].isIntersecting;
        }, { threshold: 0.2 });
        io.observe(this);

        this.onKeydown = (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (!this.mql.matches) return;
          if (!this.isInViewport) return;

          const active = document.activeElement;
          if (active) {
            const tag = active.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable) return;
          }

          if (document.querySelector('product-modal[open], .product-media-modal[open], dialog[open]')) return;

          event.preventDefault();
          this.navigateMedia(event.key === 'ArrowRight' ? 1 : -1);
        };
        document.addEventListener('keydown', this.onKeydown);
      }

      navigateMedia(direction) {
        const mediaItems = this.getMediaItems();
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const newIndex = activeIndex + direction;

        if (newIndex < 0 || newIndex >= mediaItems.length) return;

        const targetItem = mediaItems[newIndex];
        this.activateMedia(targetItem, newIndex);
      }

      activateMedia(mediaItem, index) {
        // Toggle is-active
        this.getMediaItems().forEach((el) => el.classList.remove('is-active'));
        mediaItem.classList.add('is-active');

        // Reset scroll to 0 — stacked layout on desktop, scroll is irrelevant
        if (this.mql.matches && this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollLeft = 0;
        }

        // Sync thumbnail
        if (this.elements.thumbnails) {
          const mediaId = mediaItem.dataset.mediaId;
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (thumbnail) {
            this.setActiveThumbnail(thumbnail);
            this.announceLiveRegion(mediaItem, thumbnail.dataset.mediaPosition);
          }
        }

        this.playActiveMedia(mediaItem);
        this.updateGalleryNavButtons();
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
      }

      updateGalleryNavButtons() {
        if (!this.navPrevButton || !this.navNextButton) return;
        const mediaItems = this.getMediaItems();
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const activeItem = mediaItems[activeIndex];
        const isVideo = activeItem && activeItem.querySelector('.media-type-video, .media-type-external_video');
        const buttonsContainer = this.navPrevButton.closest('.slider-buttons');

        if (buttonsContainer) buttonsContainer.classList.toggle('slider-buttons--hidden', !!isVideo);

        this.navPrevButton.toggleAttribute('disabled', activeIndex <= 0);
        this.navNextButton.toggleAttribute('disabled', activeIndex >= mediaItems.length - 1);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);

        this.updateGalleryNavButtons();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
